import { useCallback, useRef, useState } from "react";
import Link from "next/link";
import { ChevronDown, ExternalLink, List } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  NoxusJsonView,
  tryParseJsonObject,
} from "@/components/UI/NoxusJsonView";
import { CopyButton } from "@/components/UI/CopyButton";
import { IoFileRow, coerceIoFileLike, getIoFileHref } from "./ioFileDisplay";
import { SkippedBadge, isSkippedValue } from "./skippedDisplay";
import { IoEmptyValue } from "./ioEmptyValue";

export type IoListPreviewDensity = "compact" | "comfortable";

const DENSITY_STYLES = {
  compact: {
    container:
      "rounded-md border border-border/40 bg-card divide-y divide-border/40 overflow-hidden",
    row: "flex min-w-0",
    // Line-number column: tinted, ruled on the right, digits right-aligned.
    gutter:
      "flex w-5 shrink-0 justify-end select-none bg-muted/50 border-r border-border/40 px-1 py-1",
    content: "flex-1 min-w-0 py-1 pl-1.5 pr-1.5",
    trailing: "flex shrink-0 items-start py-1 pr-1.5",
    // Height of one body line — centers the trailing actions against the
    // first line of (possibly multi-line) row content.
    firstLine: "h-[1.375em] text-[10px]",
    // Same font metrics as `body`, so an inline index shares its baseline
    // with the first line of content instead of being centered near it.
    line: "text-[10px] leading-snug",
    index: "font-mono text-[9px] text-muted-foreground/60 tabular-nums",
    body: "text-[10px] text-foreground/85 leading-snug whitespace-pre-wrap break-words",
    mono: "font-mono tabular-nums",
    clamp: "line-clamp-2",
    link: "text-[10px] text-foreground/85 hover:text-foreground hover:underline leading-snug line-clamp-2",
    footer: "mt-1 pl-0.5 text-[10px] text-muted-foreground/70",
    icon: "size-2.5",
    nested: "mt-1",
  },
  comfortable: {
    container:
      "rounded-md border border-border/60 bg-card divide-y divide-border/50 overflow-hidden",
    row: "flex min-w-0",
    gutter:
      "flex w-9 shrink-0 justify-end select-none bg-muted/50 border-r border-border/40 px-2 py-1.5",
    content: "flex-1 min-w-0 py-1.5 pl-3 pr-2",
    trailing: "flex shrink-0 items-start py-1.5 pr-2",
    firstLine: "h-[1.625em] text-sm",
    line: "text-sm leading-relaxed",
    index: "font-mono text-[11px] text-muted-foreground/60 tabular-nums",
    body: "text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words",
    mono: "font-mono text-[13px] tabular-nums",
    clamp: "line-clamp-1",
    link: "text-sm text-foreground hover:underline leading-relaxed break-words",
    footer: "mt-1.5 pl-0.5 text-xs text-muted-foreground",
    icon: "size-3.5",
    nested: "mt-2",
  },
} as const;

type ListItemDisplay = {
  title: string;
  href: string | null;
  file: ReturnType<typeof coerceIoFileLike>;
  // Title fell back to JSON.stringify — no curated text/name/url shape.
  serialized?: boolean;
};

export function CompactListPreview({
  items,
  dataType,
  maxVisible,
  density = "compact",
  className,
  stopClickPropagation = true,
  totalItems,
  rawItems = false,
}: {
  items: unknown[];
  dataType: string;
  maxVisible?: number;
  density?: IoListPreviewDensity;
  className?: string;
  // The node preview panel opens its modal on click anywhere — it opts out so
  // item clicks bubble up. Interactive children (links, toggles) still stop
  // their own clicks.
  stopClickPropagation?: boolean;
  // Full list length when `items` is an endpoint-truncated preview — keeps
  // the "+N more" footer honest about items that were never sent.
  totalItems?: number;
  // Render items as raw text instead of parsed JSON trees ("show raw").
  rawItems?: boolean;
}) {
  const total = Math.max(totalItems ?? items.length, items.length);
  if (total === 0) {
    return (
      <IoEmptyValue
        label="Empty list"
        density={density}
        className={className}
      />
    );
  }
  const baseLimit = maxVisible ?? items.length;
  // When only ONE item would be hidden, show it instead of a "+1 more" footer —
  // the footer costs as much vertical space as the item itself. Only collapse
  // into "+N more" once it actually saves room (2+ hidden).
  const limit = total === baseLimit + 1 ? items.length : baseLimit;
  const visible = items.slice(0, limit);
  const remaining = total - visible.length;
  const styles = DENSITY_STYLES[density];

  return (
    <div
      className={className}
      onClick={stopClickPropagation ? (e) => e.stopPropagation() : undefined}
    >
      <div className={styles.container}>
        {visible.map((item, index) => (
          <ListItemRow
            key={index}
            index={index}
            item={item}
            dataType={dataType}
            maxVisible={maxVisible}
            density={density}
            rawItems={rawItems}
          />
        ))}
      </div>
      {remaining > 0 && (
        <p className={styles.footer}>{formatMoreLabel(remaining, dataType)}</p>
      )}
    </div>
  );
}

function itemNoun(count: number, dataType: string): string {
  const noun = dataType === "File" ? "file" : "item";
  return count === 1 ? noun : `${noun}s`;
}

function formatMoreLabel(remaining: number, dataType: string): string {
  return `+ ${remaining} more ${itemNoun(remaining, dataType)}`;
}

function listItemDisplay(item: unknown): ListItemDisplay {
  const file = coerceIoFileLike(item);
  if (file) {
    return {
      title: file.name,
      href: getIoFileHref(file, true) || null,
      file,
    };
  }

  if (typeof item === "string") {
    const trimmed = item.trim();
    const isUrl = /^https?:\/\//i.test(trimmed);
    return { title: trimmed, href: isUrl ? trimmed : null, file: null };
  }

  if (item && typeof item === "object" && !Array.isArray(item)) {
    const record = item as Record<string, unknown>;
    if (typeof record.text === "string") {
      return { title: record.text, href: null, file: null };
    }
    const curated =
      (typeof record.title === "string" && record.title) ||
      (typeof record.name === "string" && record.name) ||
      (typeof record.label === "string" && record.label) ||
      (typeof record.url === "string" && record.url) ||
      null;
    const title = curated ?? formatListItemPreview(item);
    const rawUrl =
      (typeof record.url === "string" && record.url) ||
      (typeof record.uri === "string" && record.uri) ||
      (typeof record.href === "string" && record.href) ||
      null;
    const href =
      rawUrl && /^https?:\/\//i.test(rawUrl)
        ? rawUrl
        : rawUrl
          ? getIoFileHref({ name: title, uri: rawUrl }, true) || null
          : null;
    return { title, href, file: null, serialized: curated === null };
  }

  return {
    title: formatListItemPreview(item),
    href: null,
    file: null,
  };
}

function RowShell({
  index,
  density,
  interactive = false,
  align = "baseline",
  trailing,
  children,
}: {
  index: number;
  density: IoListPreviewDensity;
  interactive?: boolean;
  // Text rows sit the index on the first line's baseline; rows whose content
  // is a single control (file chip, badge) center it against that control.
  align?: "baseline" | "center";
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  const styles = DENSITY_STYLES[density];
  return (
    <div
      className={cn(
        "group",
        styles.row,
        interactive && "transition-colors hover:bg-muted/30"
      )}
    >
      <span className={cn(styles.gutter, align === "center" && "items-center")}>
        <span className={styles.line}>
          <span className={styles.index}>{index + 1}</span>
        </span>
      </span>
      <div className={styles.content}>{children}</div>
      {trailing && (
        <span className={styles.trailing}>
          <span className={cn("flex items-center gap-0.5", styles.firstLine)}>
            {trailing}
          </span>
        </span>
      )}
    </div>
  );
}

function ExpandToggle({
  expanded,
  onToggle,
  density,
}: {
  expanded: boolean;
  onToggle: (e: React.MouseEvent) => void;
  density: IoListPreviewDensity;
}) {
  const styles = DENSITY_STYLES[density];
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={expanded ? "Collapse item" : "Expand item"}
      onClick={onToggle}
      className="inline-flex items-center justify-center rounded p-0.5 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
    >
      <ChevronDown
        className={cn(
          styles.icon,
          "transition-transform",
          expanded && "rotate-180"
        )}
        strokeWidth={1.75}
      />
    </button>
  );
}

function NestedGroupRow({
  index,
  items,
  dataType,
  maxVisible,
  density,
  rawItems,
}: {
  index: number;
  items: unknown[];
  dataType: string;
  maxVisible?: number;
  density: IoListPreviewDensity;
  rawItems: boolean;
}) {
  // Collapsed by default — nested lists can be arbitrarily deep, so don't
  // expand them all at once. The user drills in by clicking the group row.
  const [expanded, setExpanded] = useState(false);
  const styles = DENSITY_STYLES[density];
  const canExpand = items.length > 0;
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <RowShell
      index={index}
      density={density}
      interactive={canExpand}
      trailing={
        canExpand && (
          <ExpandToggle
            expanded={expanded}
            onToggle={toggle}
            density={density}
          />
        )
      }
    >
      <button
        type="button"
        disabled={!canExpand}
        onClick={toggle}
        className={cn(
          "flex w-full items-center gap-1.5 text-left text-muted-foreground",
          styles.firstLine,
          canExpand ? "cursor-pointer" : "cursor-default"
        )}
      >
        <List className={cn(styles.icon, "shrink-0")} strokeWidth={1.75} />
        <span className={cn(styles.body, "text-muted-foreground")}>
          {`${items.length} ${itemNoun(items.length, dataType)}`}
        </span>
      </button>
      {expanded && (
        <CompactListPreview
          items={items}
          dataType={dataType}
          maxVisible={maxVisible}
          density={density}
          className={styles.nested}
          rawItems={rawItems}
        />
      )}
    </RowShell>
  );
}

// Reports whether the element it is attached to clips its content (CSS
// line-clamp or horizontal truncation). Attached via callback ref so the
// measurement follows the element across remounts.
function useIsClamped(): [(el: HTMLElement | null) => void, boolean] {
  const [clamped, setClamped] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);
  const attach = useCallback((el: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    const measure = () =>
      setClamped(
        el.scrollHeight > el.clientHeight + 1 ||
          el.scrollWidth > el.clientWidth + 1
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    observerRef.current = observer;
  }, []);
  return [attach, clamped];
}

function ExpandableTextRow({
  index,
  preview,
  structured,
  copyText,
  tone,
  density,
  children,
}: {
  index: number;
  preview: string;
  // Expanded view is a different representation (JSON tree), so the toggle
  // is meaningful even when the one-line preview is not clipped.
  structured: boolean;
  copyText: string | null;
  tone: string | undefined;
  density: IoListPreviewDensity;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const [previewRef, clamped] = useIsClamped();
  const styles = DENSITY_STYLES[density];
  const canExpand = structured || clamped || preview.includes("\n");

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <RowShell
      index={index}
      density={density}
      interactive={canExpand || copyText !== null}
      trailing={
        (copyText !== null || canExpand) && (
          <>
            {copyText !== null && (
              <CopyButton
                value={copyText}
                className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              />
            )}
            {canExpand && (
              <ExpandToggle
                expanded={expanded}
                onToggle={toggle}
                density={density}
              />
            )}
          </>
        )
      }
    >
      {expanded ? (
        <div onClick={toggle}>{children}</div>
      ) : (
        <p
          ref={previewRef}
          onClick={canExpand ? toggle : undefined}
          className={cn(
            styles.body,
            styles.clamp,
            canExpand && "cursor-pointer"
          )}
        >
          <span className={tone}>{preview}</span>
        </p>
      )}
    </RowShell>
  );
}

function ListItemRow({
  index,
  item,
  dataType,
  maxVisible,
  density,
  rawItems,
}: {
  index: number;
  item: unknown;
  dataType: string;
  maxVisible?: number;
  density: IoListPreviewDensity;
  rawItems: boolean;
}) {
  const styles = DENSITY_STYLES[density];

  if (Array.isArray(item)) {
    return (
      <NestedGroupRow
        index={index}
        items={item}
        dataType={dataType}
        maxVisible={maxVisible}
        density={density}
        rawItems={rawItems}
      />
    );
  }

  // A skipped iteration slot — show the Skipped chip rather than its raw
  // `{ "skipped": true }` shape so positional alignment reads cleanly.
  if (isSkippedValue(item)) {
    return (
      <RowShell index={index} density={density} align="center">
        <SkippedBadge compact={density === "compact"} />
      </RowShell>
    );
  }

  const objectTyped =
    (dataType === "object" || dataType === "dict") &&
    item !== null &&
    typeof item === "object";
  const display = objectTyped
    ? {
        title: formatListItemPreview(item),
        href: null,
        file: null,
        serialized: true,
      }
    : listItemDisplay(item);

  if (display.file) {
    return (
      <RowShell index={index} density={density} align="center">
        <IoFileRow
          value={display.file}
          compact={density === "compact"}
          showDownload={density === "comfortable"}
          // The list row is the frame; strip the chip's own box.
          className="h-auto min-w-0 rounded-none border-0 px-0 shadow-none"
        />
      </RowShell>
    );
  }

  const comfortable = density === "comfortable";
  const copyText =
    comfortable &&
    (typeof item === "string" ||
      typeof item === "number" ||
      typeof item === "boolean")
      ? String(item)
      : null;
  const tone = valueTone(item, styles.mono);

  if (display.href) {
    return (
      <RowShell
        index={index}
        density={density}
        trailing={
          <>
            {copyText !== null && (
              <CopyButton
                value={copyText}
                className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              />
            )}
            <ExternalLink
              className={cn(styles.icon, "text-muted-foreground/50")}
              strokeWidth={1.5}
            />
          </>
        }
      >
        <Link
          href={display.href}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(styles.link, "block min-w-0")}
          onClick={(e) => e.stopPropagation()}
        >
          {display.title}
        </Link>
      </RowShell>
    );
  }

  // Compact rows (node canvas) are static: a click anywhere on the panel
  // already opens the full Run output modal.
  if (!comfortable) {
    return (
      <RowShell index={index} density={density}>
        <p className={cn(styles.body, styles.clamp)}>
          <span className={tone}>{display.title}</span>
        </p>
      </RowShell>
    );
  }

  const objectItem =
    display.serialized === true && item !== null && typeof item === "object"
      ? (item as object)
      : null;
  const jsonTreeItem =
    objectItem ??
    (!rawItems && typeof item === "string" ? tryParseJsonObject(item) : null);

  return (
    <ExpandableTextRow
      index={index}
      preview={display.title}
      structured={jsonTreeItem !== null}
      copyText={copyText}
      tone={tone}
      density={density}
    >
      {jsonTreeItem ? (
        <div
          className="min-w-0 max-w-full overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <NoxusJsonView data={jsonTreeItem} expand={2} />
        </div>
      ) : (
        <p className={cn(styles.body, "cursor-pointer")}>
          <span className={tone}>{display.title}</span>
        </p>
      )}
    </ExpandableTextRow>
  );
}

// Numbers and booleans set in the data face; nulls read as absence, not text.
function valueTone(item: unknown, mono: string): string | undefined {
  if (typeof item === "number" || typeof item === "boolean") return mono;
  if (item === null || item === undefined) {
    return "italic text-muted-foreground";
  }
  return undefined;
}

function formatListItemPreview(item: unknown): string {
  if (typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }
  if (item === null) return "null";
  if (item === undefined) return "-";
  try {
    const serialized = JSON.stringify(item);
    return serialized.length > 120
      ? `${serialized.slice(0, 117)}…`
      : serialized;
  } catch {
    return "-";
  }
}
